import { useEffect, useState } from "react";
import { CheckCircle2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { api, formatApiErrorDetail } from "@/lib/api";
import { MODULE_QUIZ } from "@/constants/testIds";

// Short quiz shown after a module's video is watched. A 100% score is
// required to mark the module complete and unlock the next one.
export default function ModuleQuiz({ moduleId, participantId, onPassed, onContinue }) {
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/training/modules/${moduleId}/quiz`).then((res) => setQuestions(res.data)).finally(() => setLoading(false));
  }, [moduleId]);

  const allAnswered = questions.length > 0 && questions.every((q) => answers[q.id]);

  const handleSubmit = async () => {
    setSubmitting(true);
    setError("");
    try {
      const payload = {
        participant_id: participantId,
        answers: questions.map((q) => ({ question_id: q.id, selected_option: answers[q.id] || null })),
      };
      const { data } = await api.post(`/training/modules/${moduleId}/quiz/submit`, payload);
      setResult(data);
      if (data.passed) onPassed && onPassed(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRetry = () => {
    setAnswers({});
    setResult(null);
  };

  if (loading) return <p className="text-sm text-slate-400">Loading module quiz...</p>;

  if (result?.passed) {
    return (
      <div className="space-y-3">
        <div
          data-testid={MODULE_QUIZ.passedMessage(moduleId)}
          className="flex items-center gap-2 text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md p-4 text-sm font-semibold"
        >
          <CheckCircle2 className="h-4 w-4 shrink-0" /> {result.score}/{result.total_questions} — 100%! This module is complete.
        </div>
        <Button
          data-testid={MODULE_QUIZ.continueButton(moduleId)}
          onClick={() => onContinue && onContinue()}
          className="w-full bg-denso-red hover:bg-denso-lightred text-white"
        >
          Continue
        </Button>
      </div>
    );
  }

  if (result && !result.passed) {
    return (
      <div data-testid={MODULE_QUIZ.failedMessage(moduleId)} className="space-y-3 bg-red-50 border border-red-200 rounded-md p-4">
        <p className="text-sm font-semibold text-red-700 flex items-center gap-1.5">
          <XCircle className="h-4 w-4" /> {result.score}/{result.total_questions} — you need 100% to continue.
        </p>
        {result.answers.filter((a) => !a.is_correct).map((a) => (
          <div key={a.question_id} className="text-xs text-slate-600 bg-white rounded p-2">
            <p className="font-medium text-slate-800">{a.question_text}</p>
            <p>Correct answer: <span className="text-emerald-700 font-medium">{a.options[a.correct_option]}</span></p>
          </div>
        ))}
        <Button size="sm" data-testid={MODULE_QUIZ.retryButton(moduleId)} onClick={handleRetry} className="bg-denso-red hover:bg-denso-lightred text-white">
          Try Again
        </Button>
      </div>
    );
  }

  return (
    <div data-testid={MODULE_QUIZ.container(moduleId)} className="space-y-4 bg-slate-50 rounded-md p-4 border border-slate-200">
      <p className="text-xs font-semibold uppercase tracking-wide text-denso-red">Module Quiz — score 100% to continue</p>
      {questions.map((q) => (
        <div key={q.id} className="space-y-2">
          <p className="text-sm font-medium text-slate-800">{q.question}</p>
          <RadioGroup value={answers[q.id] || ""} onValueChange={(v) => setAnswers((prev) => ({ ...prev, [q.id]: v }))}>
            {Object.entries(q.options).map(([key, label]) => (
              <label
                key={key}
                htmlFor={`m-${moduleId}-${q.id}-${key}`}
                className={`flex items-center gap-2 rounded-md border px-3 py-2 text-sm cursor-pointer transition-colors duration-150 ${
                  answers[q.id] === key ? "border-denso-red bg-red-50/60" : "border-slate-200 hover:border-slate-300"
                }`}
              >
                <RadioGroupItem value={key} id={`m-${moduleId}-${q.id}-${key}`} data-testid={MODULE_QUIZ.optionRadio(moduleId, q.id, key)} />
                {label}
              </label>
            ))}
          </RadioGroup>
        </div>
      ))}
      {error && <p className="text-xs text-red-600">{error}</p>}
      <Button
        data-testid={MODULE_QUIZ.submitButton(moduleId)}
        disabled={!allAnswered || submitting}
        onClick={handleSubmit}
        className="bg-denso-red hover:bg-denso-lightred text-white"
      >
        {submitting ? "Checking..." : "Submit Quiz"}
      </Button>
    </div>
  );
}
